import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Bookmark, Sparkles, Check, MessageCircle, Feather } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { LESSON } from "@/constants/testIds";

function ProgressBar({ current, total }) {
  const pct = total ? ((current + 1) / total) * 100 : 0;
  return (
    <div className="h-1.5 bg-[#E5DED4] rounded-full overflow-hidden max-w-3xl mx-auto w-full">
      <motion.div className="h-full bg-[#C89B55]" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
    </div>
  );
}

export default function LessonReader() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [reflection, setReflection] = useState("");

  useEffect(() => {
    track("lesson_started", { lesson_id: lessonId });
    api.get(`/lessons/${lessonId}`).then(({ data }) => setLesson(data)).catch((e) => {
      if (e?.response?.status === 402) {
        toast("Unlock all of Sisterhood University", { description: "This course is part of full membership. Subscribe to open it." });
        navigate("/paywall");
      } else {
        navigate("/learn");
      }
    });
    setStep(0);
  }, [lessonId]);

  const steps = useMemo(() => {
    if (!lesson) return [];
    const s = [{ type: "welcome" }];
    lesson.sections.forEach((_, i) => s.push({ type: "section", index: i }));
    s.push({ type: "takeaways" }, { type: "reflection" }, { type: "exercise" }, { type: "complete" });
    return s;
  }, [lesson]);

  const bookmark = async () => {
    const { data } = await api.post(`/lessons/${lessonId}/bookmark`);
    setLesson((l) => ({ ...l, bookmarked: data.bookmarked }));
  };

  const complete = async () => {
    if (!lesson.completed) {
      await api.post(`/lessons/${lessonId}/complete`);
      setLesson((l) => ({ ...l, completed: true }));
      track("lesson_completed", { lesson_id: lessonId });
    }
    if (reflection.trim()) {
      await api.post("/journal", { title: lesson.title, body: reflection, prompt_text: lesson.reflection }).catch(() => {});
    }
  };
  const onFinish = async () => { await complete(); toast.success("Lesson complete. Well done."); navigate("/learn"); };
  const onCoach = async () => { await complete(); navigate("/coach"); };

  if (!lesson) return <div className="h-64 flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-[#C89B55] border-t-transparent animate-spin" /></div>;

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const goNext = () => { if (step < steps.length - 1) { setDir(1); setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const goBack = () => { if (step > 0) { setDir(-1); setStep(step - 1); window.scrollTo({ top: 0, behavior: "smooth" }); } else navigate(-1); };

  const renderStep = () => {
    if (current.type === "welcome") return (
      <div className="text-center pt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#C89B55] font-semibold mb-3">{lesson.minutes} min · a step at a time</p>
        <h1 className="font-heading text-4xl leading-tight">{lesson.title}</h1>
        <p className="text-[#59535B] mt-4 text-lg leading-relaxed max-w-md mx-auto">{lesson.summary}</p>
      </div>
    );
    if (current.type === "section") {
      const sec = lesson.sections[current.index];
      return (
        <div className="pt-4">
          <p className="text-[10px] uppercase tracking-widest text-[#291B2D]/50 font-semibold mb-3 flex items-center gap-2"><Sparkles size={12} /> Core learning</p>
          <h2 className="font-heading text-2xl sm:text-3xl mb-4">{sec.heading}</h2>
          <div className="bg-white rounded-3xl p-7 card-soft text-[#252326] leading-[1.8] text-[17px]">{sec.body}</div>
        </div>
      );
    }
    if (current.type === "takeaways") return (
      <div className="pt-4">
        <p className="text-[10px] uppercase tracking-widest text-[#C89B55] font-semibold mb-3">Key takeaways</p>
        <div className="bg-[#F7F2EA] border border-[#E5DED4] rounded-3xl p-7 space-y-3">
          {lesson.takeaways.map((t, i) => (
            <div key={i} className="flex items-start gap-3 text-[#252326]"><span className="h-1.5 w-1.5 rounded-full bg-[#C89B55] mt-2.5 shrink-0" />{t}</div>
          ))}
        </div>
      </div>
    );
    if (current.type === "reflection") return (
      <div className="pt-4">
        <p className="text-[10px] uppercase tracking-widest text-[#B97978] font-semibold mb-3">Reflection</p>
        <h2 className="font-heading text-2xl sm:text-3xl mb-6">{lesson.reflection}</h2>
        <textarea rows={7} value={reflection} onChange={(e) => setReflection(e.target.value)}
          data-testid="reflection-textarea" placeholder="Write your thoughts here — private, and saved to your journal when you finish."
          className="w-full bg-white border border-[#291B2D]/10 rounded-2xl p-5 focus:outline-none focus:ring-2 focus:ring-[#C89B55] leading-relaxed text-[16px]" />
      </div>
    );
    if (current.type === "exercise") return (
      <div className="pt-4">
        <p className="text-[10px] uppercase tracking-widest text-[#3D6B52] font-semibold mb-3">Try this</p>
        <div className="bg-[#291B2D] text-[#FCFAF6] rounded-3xl p-8">
          <Feather className="h-6 w-6 text-[#C89B55] mb-4" />
          <p className="text-lg leading-relaxed">{lesson.exercise}</p>
        </div>
      </div>
    );
    if (current.type === "complete") return (
      <div className="text-center pt-8">
        <div className="h-16 w-16 rounded-full bg-[#ECF3EE] text-[#3D6B52] flex items-center justify-center mx-auto mb-5"><Check className="h-8 w-8" /></div>
        <h2 className="font-heading text-3xl">You did the work.</h2>
        <p className="text-[#59535B] mt-3 max-w-md mx-auto">Small, brave steps become the woman you're becoming. Keep going.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button data-testid={LESSON.completeButton} onClick={onFinish}
            className="rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-7 py-3.5 font-medium transition-smooth">
            {lesson.completed ? "Finish" : "Mark complete & finish"}
          </button>
          <button onClick={onCoach}
            className="rounded-full bg-white border border-[#291B2D]/10 hover:bg-[#F7F2EA] px-7 py-3.5 font-medium transition-smooth flex items-center justify-center gap-2">
            <MessageCircle className="h-4 w-4" /> Talk it through with your coach
          </button>
        </div>
      </div>
    );
    return null;
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col">
      <ProgressBar current={step} total={steps.length} />
      <div className="flex-1 max-w-3xl w-full mx-auto pb-32">
        <div className="flex items-center justify-between mt-4 mb-6">
          <button data-testid={LESSON.backButton} onClick={goBack} className="flex items-center gap-2 text-sm text-[#59535B] hover:text-[#252326] transition-smooth">
            <ArrowLeft size={14} /> {step === 0 ? "Back" : "Previous"}
          </button>
          <button data-testid={LESSON.bookmarkButton} onClick={bookmark} aria-label="Bookmark lesson"
            className={`p-2.5 rounded-full border transition-smooth ${lesson.bookmarked ? "bg-[#C89B55] border-[#C89B55]" : "border-[#E5DED4] hover:bg-[#F7F2EA]"}`}>
            <Bookmark className={`h-4 w-4 ${lesson.bookmarked ? "text-[#291B2D] fill-[#291B2D]" : "text-[#59535B]"}`} />
          </button>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={step} initial={{ opacity: 0, x: dir * 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -40 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} data-testid={`step-${current?.type}`}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
      {!isLast && (
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 bg-[#FCFAF6]/95 backdrop-blur border-t border-[#291B2D]/5 px-6 py-4 z-30">
          <div className="max-w-3xl mx-auto">
            <button onClick={goNext} data-testid="lesson-next"
              className="w-full bg-[#291B2D] text-white rounded-full py-4 font-medium hover:bg-[#352238] transition-smooth flex items-center justify-center gap-2 text-base">
              {step === 0 ? "Begin lesson" : "Next"} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
